import { useState, useEffect } from 'react';
import { useNavigate, Link } from '@tanstack/react-router';
import { z } from 'zod';
import { useAuth } from '@/hooks/useAuth';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useToast } from '@/hooks/use-toast';
import { Loader2, ArrowLeft } from 'lucide-react';
import { useTranslation } from '@/hooks/useTypedTranslation';

const authSchema = z.object({
  email: z.string().email('Please enter a valid email address'),
  password: z.string().min(6, 'Password must be at least 6 characters'),
});

export default function Auth() {
  const [isLogin, setIsLogin] = useState(true);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [errors, setErrors] = useState<{ email?: string; password?: string }>({});
  const [submitting, setSubmitting] = useState(false);
  
  const { user, loading, signIn, signUp } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { t } = useTranslation();

  // Auth redirect — kept as useEffect because it depends on async auth state
  useEffect(() => {
    if (!loading && user) {
      navigate({ to: '/admin' });
    }
  }, [user, loading, navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrors({});
    
    const result = authSchema.safeParse({ email, password });
    if (!result.success) {
      const fieldErrors: { email?: string; password?: string } = {};
      result.error.errors.forEach((err) => {
        if (err.path[0] === 'email') fieldErrors.email = err.message;
        if (err.path[0] === 'password') fieldErrors.password = err.message;
      });
      setErrors(fieldErrors);
      return;
    }

    setSubmitting(true);
    
    const { error } = isLogin 
      ? await signIn(email, password)
      : await signUp(email, password);
    
    setSubmitting(false);

    if (error) {
      let message = error.message;
      if (error.message.includes('Invalid login credentials')) {
        message = t.auth?.invalidCredentials || 'Invalid email or password';
      } else if (error.message.includes('User already registered')) {
        message = t.auth?.userExists || 'This email is already registered, please login directly';
      }
      toast({ title: t.common.error, description: message, variant: 'destructive' });
    } else if (!isLogin) {
      toast({ title: t.auth?.registerSuccess || 'Registration successful', description: t.auth?.pleaseLogin || 'Account created, please login' });
      setIsLogin(true);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-background px-4">
      <div className="w-full max-w-md">
        <Link to="/" className="inline-flex items-center text-muted-foreground hover:text-foreground mb-8">
          <ArrowLeft className="w-4 h-4 mr-2" />
          {t.common.backToHome}
        </Link>
        
        <div className="bg-card border border-border rounded-2xl p-8">
          <h1 className="text-2xl font-bold text-foreground mb-2">
            {isLogin ? t.auth?.login || 'Login' : t.auth?.register || 'Register'}
          </h1>
          <p className="text-muted-foreground mb-6">
            {isLogin ? t.auth?.loginToManage || 'Login to manage project content' : t.auth?.createNewAccount || 'Create new account'}
          </p>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="email">{t.auth?.email || 'Email'}</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="your@email.com"
                className="mt-1"
              />
              {errors.email && (
                <p className="text-sm text-destructive mt-1">{errors.email}</p>
              )}
            </div>

            <div>
              <Label htmlFor="password">{t.auth?.password || 'Password'}</Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                className="mt-1"
              />
              {errors.password && (
                <p className="text-sm text-destructive mt-1">{errors.password}</p>
              )}
            </div>

            <Button type="submit" className="w-full" disabled={submitting}>
              {submitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              {isLogin ? t.auth?.login || 'Login' : t.auth?.register || 'Register'}
            </Button>
          </form>

          <div className="mt-6 text-center">
            <button
              type="button"
              onClick={() => setIsLogin(!isLogin)}
              className="text-sm text-muted-foreground hover:text-foreground"
            >
              {isLogin ? (t.auth?.noAccount || 'No account? Click to register') : (t.auth?.hasAccount || 'Already have an account? Click to login')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
